import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Paper,
  Box,
} from "@mui/material";
import serviceEnquiryService from "../../services/serviceEnquiryService";
import "../../../src/App.css";
import moment from "moment";

const ServiceEnquiry = ({ enquiryId }) => {
  const [enquiryFormData, setEnquiryFormData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const convertUTCToLocal = useCallback((date, format = "MM-DD-YYYY, HH:mm:ss") => {
    if (!date) return "N/A";
    const localDate = moment.utc(date).local();
    return localDate.format(format);
  }, []);

  const fetchServiceEnquiryForm = useCallback(async () => {
    if (!enquiryId) {
      setError("No enquiry ID provided");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await serviceEnquiryService.serviceEnquiryForm(enquiryId);
      setEnquiryFormData(result?.data?.successList || []);
    } catch (err) {
      console.error("Error fetching service enquiry form:", err);
      setError("Failed to load enquiry data");
      setEnquiryFormData([]);
    } finally {
      setIsLoading(false);
    }
  }, [enquiryId]);

  useEffect(() => {
    fetchServiceEnquiryForm();
  }, [fetchServiceEnquiryForm]);

  const tableHeaders = [
    { id: "statusName", label: "Status Name", align: "center" },
    { id: "statusDate", label: "Status Date", align: "center" },
    { id: "createdBy", label: "Created By", align: "center" },
  ];

  const headerStyle = {
    fontSize: "15px",
    fontWeight: 600,
    textAlign: "center",
    backgroundColor: "#f5f5f5",
  };

  const cellStyle = {
    fontSize: "13px",
    fontWeight: 500,
    textAlign: "center",
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography>Loading enquiry data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!enquiryFormData || enquiryFormData.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography>No enquiry data available</Typography>
      </Box>
    );
  }

  return (
    <Box component="section" aria-label="Service Enquiry Details">
      <Box 
        className="w-118 h-10 top-98 left-47 bg-lightBlue bg-opacity-5.5 overflow-auto"
        mb={2}
      >
        <Typography 
          className="font-poppins text-base font-semibold leading-6 mt-2 ml-2 text-left"
          component="h2"
          variant="h6"
        >
          Enquiry Id - {enquiryId}
        </Typography>
      </Box>
      
      <TableContainer
        component={Paper}
        sx={{ 
          width: "100%", 
          maxHeight: "36vw", 
          overflow: "auto",
          '& .MuiTable-root': {
            minWidth: 650,
          }
        }}
      >
        <Table aria-label="service enquiry status table" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell
                  key={header.id}
                  style={headerStyle}
                  align={header.align}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {enquiryFormData.map((notifi) => (
              <TableRow 
                key={notifi.id || notifi.statusDate}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell style={cellStyle}>
                  {notifi?.statusName || "N/A"}
                </TableCell>
                <TableCell style={cellStyle}>
                  {convertUTCToLocal(notifi?.statusDate)}
                </TableCell>
                <TableCell style={cellStyle}>
                  {notifi?.userName || "N/A"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
    </Box>
  );
};

ServiceEnquiry.propTypes = {
  enquiryId: PropTypes.string.isRequired,
};

export default ServiceEnquiry;